import { formatInTimeZone } from 'date-fns-tz/formatInTimeZone';
import { addMonths } from 'date-fns/addMonths';
import { format } from 'date-fns/format';
import { isValid } from 'date-fns/isValid';
import { parse } from 'date-fns/parse';
import { parseISO } from 'date-fns/parseISO';

import { serializeDate } from '@/lib/dateFormatter';
import { RecurrenceByDay, RecurrenceFrequency } from '@/types/enums';

export const recurrenceFrequencies = Object.values(RecurrenceFrequency) as RecurrenceFrequency[];

export const recurrenceByDayValues = Object.values(RecurrenceByDay) as RecurrenceByDay[];

export type RecurrenceEndCondition = 'never' | 'until' | 'count';

export type RecurrenceParseComponents = {
  frequency?: unknown;
  interval?: unknown;
  byDay?: unknown;
  byMonth?: unknown;
  byMonthDay?: unknown;
  bySetPos?: unknown;
  count?: unknown;
  until?: unknown;
  weekStart?: unknown;
};

type RecurrenceAdvancedRulePart = {
  key: string;
  value: string;
};

export interface RecurrenceRuleState {
  frequency: RecurrenceFrequency;
  interval: number | null;
  endCondition: RecurrenceEndCondition;
  until: string;
  /** How many times the series happens, for the `count` end condition. */
  count?: number | null;
  byDay: RecurrenceByDay[];
  advancedRuleParts?: RecurrenceAdvancedRulePart[];
}

export type RecurrenceScheduleContext = {
  /** The Event Schedule start, as an ISO datetime or a `YYYY-MM-DD` calendar date. */
  startDate?: unknown;
  /** The timezone the series repeats in. */
  timezone?: string;
};

const intervalMin = 1;
const intervalMax = 999;

export const countMin = 1;
export const countMax = 999;

export const recurrenceUntilBeforeStartError =
  'The recurrence end date must be on or after the event start date.';

export function getDefaultRecurrenceState(): RecurrenceRuleState {
  return {
    frequency: 'DAILY',
    interval: 1,
    endCondition: 'never',
    until: '',
    byDay: []
  };
}

export function isFrequency(value: unknown): value is RecurrenceFrequency {
  return typeof value === 'string' && recurrenceFrequencies.includes(value as RecurrenceFrequency);
}

export function clampInterval(interval: unknown): number {
  const numericInterval =
    typeof interval === 'number'
      ? interval
      : typeof interval === 'string'
        ? Number.parseInt(interval, 10)
        : Number.NaN;

  if (!Number.isFinite(numericInterval)) {
    return intervalMin;
  }

  return Math.min(intervalMax, Math.max(intervalMin, Math.floor(numericInterval)));
}

export function clampCount(count: unknown): number {
  const numericCount =
    typeof count === 'number'
      ? count
      : typeof count === 'string'
        ? Number.parseInt(count, 10)
        : Number.NaN;

  if (!Number.isFinite(numericCount)) {
    return countMin;
  }

  return Math.min(countMax, Math.max(countMin, Math.floor(numericCount)));
}

export function normalizeByDay(byDay: unknown): RecurrenceByDay[] {
  if (!Array.isArray(byDay)) {
    return [];
  }

  return recurrenceByDayValues.filter((day) => byDay.some((value) => value === day));
}

const byDayByWeekdayIndex: RecurrenceByDay[] = ['SU', 'MO', 'TU', 'WE', 'TH', 'FR', 'SA'];

const calendarDatePattern = /^\d{4}-\d{2}-\d{2}$/;

function byDayForCalendarDate(calendarDate: string): RecurrenceByDay | null {
  const parsedCalendarDate = parse(calendarDate, 'yyyy-MM-dd', new Date());

  return isValid(parsedCalendarDate) ? byDayByWeekdayIndex[parsedCalendarDate.getDay()] : null;
}

function formatCalendarDateInTimezone(date: Date, timezone: string): string {
  try {
    return formatInTimeZone(date, timezone, 'yyyy-MM-dd');
  } catch {
    return format(date, 'yyyy-MM-dd');
  }
}

/**
 * The calendar date a schedule starts on.
 *
 * A `YYYY-MM-DD` start is already the calendar date the user picked, so it is read as-is. A full
 * datetime is an instant instead, so its calendar date only makes sense in the timezone the series
 * repeats in — near midnight the browser's timezone would land on a different day.
 */
export function resolveStartCalendarDate(startDate: unknown, timezone = 'UTC'): string | null {
  if (typeof startDate !== 'string') {
    return null;
  }

  const trimmedStartDate = startDate.trim();

  if (trimmedStartDate === '') {
    return null;
  }

  if (calendarDatePattern.test(trimmedStartDate)) {
    return trimmedStartDate;
  }

  const parsedStartDate = parseISO(trimmedStartDate);

  if (!isValid(parsedStartDate)) {
    return null;
  }

  return formatCalendarDateInTimezone(parsedStartDate, timezone);
}

/** The weekday of a schedule's start, as an RRULE BYDAY code. */
export function resolveByDayForStartDate(
  startDate: unknown,
  timezone = 'UTC'
): RecurrenceByDay | null {
  const startCalendarDate = resolveStartCalendarDate(startDate, timezone);

  return startCalendarDate === null ? null : byDayForCalendarDate(startCalendarDate);
}

/**
 * The earliest Until date a finite series may end on: a series must not end before it starts, and
 * the editor never offers a date that is already past.
 */
export function getMinimumUntilDate(
  startDate: unknown,
  timezone = 'UTC',
  now: Date = new Date()
): string {
  const todayCalendarDate = formatCalendarDateInTimezone(now, timezone);
  const startCalendarDate = resolveStartCalendarDate(startDate, timezone);

  return startCalendarDate !== null && startCalendarDate > todayCalendarDate
    ? startCalendarDate
    : todayCalendarDate;
}

/** The Until date the editor offers when the user first selects On date. */
export function getDefaultUntilDate(minimumUntilDate: string): string {
  const parsedMinimumUntilDate = parse(minimumUntilDate, 'yyyy-MM-dd', new Date());
  const defaultUntilDate = isValid(parsedMinimumUntilDate) ? parsedMinimumUntilDate : new Date();

  return format(addMonths(defaultUntilDate, 1), 'yyyy-MM-dd');
}

export function getUntilBoundsError(
  until: string,
  startDate: unknown,
  timezone = 'UTC'
): string | null {
  if (!calendarDatePattern.test(until)) {
    return null;
  }

  const startCalendarDate = resolveStartCalendarDate(startDate, timezone);

  if (startCalendarDate === null || until >= startCalendarDate) {
    return null;
  }

  return recurrenceUntilBeforeStartError;
}

export function parseUntilDateInputValue(until: unknown, timezone = 'UTC'): string {
  if (typeof until !== 'string' || until.trim() === '') {
    return '';
  }

  const trimmedUntil = until.trim();

  if (/^\d{8}T\d{6}Z$/i.test(trimmedUntil)) {
    const parsedUtcDateTime = parse(trimmedUntil.toUpperCase(), "yyyyMMdd'T'HHmmssX", new Date());

    if (isValid(parsedUtcDateTime)) {
      try {
        return formatInTimeZone(parsedUtcDateTime, timezone, 'yyyy-MM-dd');
      } catch {
        return formatInTimeZone(parsedUtcDateTime, 'UTC', 'yyyy-MM-dd');
      }
    }
  }

  if (/^\d{8}$/.test(trimmedUntil)) {
    const parsedCompactDate = parse(trimmedUntil, 'yyyyMMdd', new Date());

    return isValid(parsedCompactDate) ? format(parsedCompactDate, 'yyyy-MM-dd') : '';
  }

  if (calendarDatePattern.test(trimmedUntil)) {
    return trimmedUntil;
  }

  const parsedIsoDate = parseISO(trimmedUntil);

  if (isValid(parsedIsoDate)) {
    return format(parsedIsoDate, 'yyyy-MM-dd');
  }

  const parsedDate = new Date(trimmedUntil);
  if (!Number.isNaN(parsedDate.getTime())) {
    return serializeDate(parsedDate);
  }

  return '';
}

export function toUntilRruleValue(untilDate: string): string | null {
  if (!calendarDatePattern.test(untilDate)) {
    return null;
  }

  return untilDate.replaceAll('-', '');
}

function normalizeRulePartValue(value: unknown): string | null {
  if (typeof value === 'number' && Number.isFinite(value)) {
    return value.toString();
  }

  if (typeof value === 'string') {
    const trimmedValue = value.trim();

    return trimmedValue === '' ? null : trimmedValue.toUpperCase();
  }

  if (!Array.isArray(value)) {
    return null;
  }

  const values = value.flatMap((item) => {
    if (typeof item !== 'string' && typeof item !== 'number') {
      return [];
    }
    const normalizedItem = item.toString().trim().toUpperCase();
    return normalizedItem === '' ? [] : [normalizedItem];
  });

  return values.length > 0 ? values.join(',') : null;
}

function getAdvancedRuleParts(
  components: RecurrenceParseComponents,
  frequency: RecurrenceFrequency,
  allowCount: boolean
): RecurrenceAdvancedRulePart[] {
  const ruleParts: RecurrenceAdvancedRulePart[] = [];
  const addRulePart = (key: string, value: unknown): void => {
    const normalizedValue = normalizeRulePartValue(value);

    if (normalizedValue !== null) {
      ruleParts.push({ key, value: normalizedValue });
    }
  };

  if (!allowCount) {
    addRulePart('COUNT', components.count);
  }
  addRulePart('BYMONTHDAY', components.byMonthDay);
  addRulePart('BYMONTH', components.byMonth);
  addRulePart('BYSETPOS', components.bySetPos);
  addRulePart('WKST', components.weekStart);

  if (frequency !== 'WEEKLY') {
    addRulePart('BYDAY', components.byDay);
  }

  return ruleParts;
}

export function buildRruleFromState(state: RecurrenceRuleState): string {
  if (state.interval === null) {
    return '';
  }

  const interval = clampInterval(state.interval);
  const nextState: RecurrenceRuleState = {
    ...state,
    interval,
    byDay: normalizeByDay(state.byDay)
  };

  const ruleParts = [`FREQ=${nextState.frequency}`, `INTERVAL=${interval}`];

  if (nextState.frequency === 'WEEKLY' && nextState.byDay.length > 0) {
    ruleParts.push(`BYDAY=${nextState.byDay.join(',')}`);
  }

  if (nextState.endCondition === 'until') {
    const untilValue = toUntilRruleValue(nextState.until);
    if (untilValue) {
      ruleParts.push(`UNTIL=${untilValue}`);
    }
  }

  if (nextState.endCondition === 'count' && nextState.count != null) {
    ruleParts.push(`COUNT=${clampCount(nextState.count)}`);
  }

  for (const rulePart of nextState.advancedRuleParts ?? []) {
    if (rulePart.key === 'COUNT' && nextState.endCondition !== 'never') {
      continue;
    }

    if (rulePart.key === 'BYDAY' && nextState.frequency === 'WEEKLY') {
      continue;
    }

    if (ruleParts.some((part) => part.startsWith(`${rulePart.key}=`))) {
      continue;
    }

    ruleParts.push(`${rulePart.key}=${rulePart.value}`);
  }

  return ruleParts.join(';');
}

/**
 * With `allowCount`, a rule that ends after a number of times shows as "After N times". Without
 * it, COUNT stays an advanced rule part and the rule shows as never ending.
 */
export function hydrateStateFromComponents(
  components: RecurrenceParseComponents,
  timezone = 'UTC',
  allowCount = false
): RecurrenceRuleState {
  const defaultState = getDefaultRecurrenceState();
  const frequency = isFrequency(components.frequency)
    ? components.frequency
    : defaultState.frequency;
  const interval = clampInterval(components.interval);
  const byDay = normalizeByDay(components.byDay);
  const until = parseUntilDateInputValue(components.until, timezone);
  const count =
    allowCount && !until && components.count != null ? clampCount(components.count) : null;
  const endCondition: RecurrenceEndCondition = until ? 'until' : count !== null ? 'count' : 'never';
  const advancedRuleParts = getAdvancedRuleParts(components, frequency, allowCount);

  return {
    frequency,
    interval,
    endCondition,
    until,
    ...(count !== null ? { count } : {}),
    byDay,
    ...(advancedRuleParts.length > 0 ? { advancedRuleParts } : {})
  };
}

export function getRecurrenceStateError(
  state: RecurrenceRuleState,
  schedule: RecurrenceScheduleContext = {}
): string | null {
  if (state.frequency === 'WEEKLY' && normalizeByDay(state.byDay).length === 0) {
    return 'Select at least one weekday for weekly recurrence.';
  }

  if (state.endCondition === 'count') {
    return state.count == null ? `Enter a number of times from ${countMin} to ${countMax}.` : null;
  }

  if (state.endCondition !== 'until') {
    return null;
  }

  if (toUntilRruleValue(state.until) === null) {
    return 'Select a valid end date.';
  }

  return getUntilBoundsError(state.until, schedule.startDate, schedule.timezone ?? 'UTC');
}

function recurrenceRuleParts(rule: unknown): Map<string, string> | null {
  if (typeof rule !== 'string' || rule.trim() === '') {
    return null;
  }

  const lines = rule
    .split(/\r?\n/)
    .map((line) => line.trim())
    .filter((line) => line !== '');
  const ruleLine =
    lines.find((line) => line.toUpperCase().startsWith('RRULE:')) ??
    lines.find((line) => {
      const upper = line.toUpperCase();

      return !upper.startsWith('DTSTART') && !upper.startsWith('X-DIAGONAL-TZID:');
    }) ??
    '';
  const parts = new Map<string, string>();

  for (const part of ruleLine.replace(/^RRULE:/i, '').split(';')) {
    const [rawKey, ...rest] = part.split('=');
    const key = rawKey.trim().toUpperCase();
    const value = rest.join('=').trim().toUpperCase();

    if (key === '' || (key === 'INTERVAL' && Number(value) === 1)) {
      continue;
    }

    if (key === 'BYDAY') {
      parts.set(
        key,
        value
          .split(',')
          .map((day) => day.trim())
          .toSorted((a, b) => a.localeCompare(b))
          .join(',')
      );
    } else if (key === 'COUNT') {
      parts.set(key, String(Number(value)));
    } else if (key === 'UNTIL') {
      parts.set(key, value.slice(0, 8));
    } else {
      parts.set(key, value);
    }
  }

  return parts;
}

/**
 * Whether two stored or emitted rules repeat the same way. The server sends its rule with a
 * DTSTART line and no `INTERVAL=1`, and the editor emits an `X-DIAGONAL-TZID:` line with
 * `INTERVAL=1`, so the RRULE parts are compared in any order and spelling.
 */
export function sameRecurrenceRule(a: unknown, b: unknown): boolean {
  const left = recurrenceRuleParts(a);
  const right = recurrenceRuleParts(b);

  if (left === null || right === null) {
    return left === right;
  }

  return left.size === right.size && [...left].every(([key, value]) => right.get(key) === value);
}
